import { HOST, WORKSPACE_MENU, CURRENT_WORKSPACE } from '../engine/anchors';
import {
  closeHoverMenu,
  holdMenuOpen,
  openHoverMenu,
  type HoverMenu,
} from './hoverMenu';
import type { TaskFlow } from './types';

const MENU: HoverMenu = {
  name: 'workspaces',
  trigger: HOST.workspaceSwitcher,
  panel: WORKSPACE_MENU,
};

const ensureOpen = async (): Promise<void> => {
  await openHoverMenu(MENU);
};

let releaseHold: (() => void) | null = null;

export const WORKSPACE_SWITCHER_TASK: TaskFlow = {
  id: 'walk-workspaces',
  stepId: 'workspaces',
  cta: 'Try it: open your workspaces',
  preparingLabel: 'Opening your workspaces…',
  title: 'Workspaces — the switcher',
  summary: 'Four stops around the open panel. Nothing is switched for you.',
  doneTitle: 'That is the switcher',
  doneDescription:
    'You know where to change which business the app is showing, and where a new WhatsApp number gets added. Everything from here on — conversations, campaigns, reports — belongs to whichever workspace is selected in that box.',

  whenDone: 'resume',

  beforeStart: async () => {
    releaseHold?.();
    releaseHold = holdMenuOpen(MENU);
    if (!(await openHoverMenu(MENU))) {
      console.warn(
        '[tour-guide] "walk-workspaces": the workspace menu never opened. Its steps ' +
          'will resolve nothing and be skipped.',
      );
    }
  },

  afterEnd: async () => {
    releaseHold?.();
    releaseHold = null;
    await closeHoverMenu(MENU);
  },

  steps: [
    {
      id: 'workspace-panel',
      label: 'Workspaces',
      title: 'Every workspace you can open',
      description:
        'One row per connected WhatsApp Business number, each with the number underneath it so two lines for the same brand are told apart. This list is the whole of your access: a workspace you are not a member of is not here, and there is no other way into one. Add workspace at the bottom is how a new number joins.',
      anchor: WORKSPACE_MENU,
      side: 'right',
      spotlight: true,
      prepare: ensureOpen,
    },

    {
      id: 'workspace-current',
      label: 'Current',
      title: 'The one you are in now',
      description:
        "The tick and the green row mark the workspace every screen is currently scoped to. Clicking any other row switches to it and the whole app follows — the inbox, the CRM board, the campaign list and every number on the dashboard all become that business's. We are not switching for you: read the rows, and change it yourself whenever you want.",
      anchor: CURRENT_WORKSPACE,
      side: 'right',
      spotlight: true,
      prepare: ensureOpen,
    },

    {
      id: 'workspace-add',
      label: 'Add one',
      title: 'Add another workspace',
      description:
        'Connects a second WhatsApp Business number as its own workspace, with its own conversations, contacts and reporting. It opens the Business accounts section of Account settings, where the connection itself is made. How many you may have is set by your plan.',
      anchor: { selector: HOST.addWorkspace },
      side: 'right',
      spotlight: true,
      prepare: ensureOpen,
    },

    {
      id: 'workspace-integrations',
      label: 'Integrations',
      title: 'Integrations — connect the rest of your stack',
      description:
        'Payment gateways, your online store, calendars and the point-of-sale, each connected once here and then usable from anywhere in this workspace: a payment link inside a chat, a booking that writes to your real calendar, an order that lands in your POS. What you may connect depends on your plan.',
      anchor: { selector: HOST.workspaceIntegrations },
      side: 'right',
      spotlight: true,
      prepare: ensureOpen,
    },
  ],
};
